import api from "./axios";

export interface ClassSubject {
    id: string;
    class_id: string;
    subject_id: string;
    teacher_id: string;
    semester_id: string;
    schedule?: string;
    room?: string;
    is_active: boolean;
    display_name?: string;
    full_schedule?: string;
    created_at: string;
    updated_at: string;

    // Relationships
    class?: {
        id: string;
        grade_level: string;
        name: string;
    };
    subject?: {
        id: string;
        name: string;
        code: string;
    };
    teacher?: {
        id: string;
        nip: string;
        user: {
            id: string;
            name: string;
            email: string;
        };
    };
    semester?: {
        id: string;
        name: string;
        academic_year?: {
            id: string;
            name: string;
            year: string;
        };
    };
}

export interface CreateClassSubjectData {
    class_id: string;
    subject_id: string;
    teacher_id: string;
    semester_id: string;
    schedule?: string;
    room?: string;
    is_active?: boolean;
}

export interface UpdateClassSubjectData {
    class_id?: string;
    subject_id?: string;
    teacher_id?: string;
    semester_id?: string;
    schedule?: string;
    room?: string;
    is_active?: boolean;
}

export interface ClassSubjectFilters {
    search?: string;
    class_id?: string;
    subject_id?: string;
    teacher_id?: string;
    semester_id?: string;
    is_active?: boolean;
    page?: number;
    per_page?: number;
}

export interface ClassSubjectListResponse {
    success: boolean;
    message: string;
    data: {
        items: ClassSubject[];
        meta: {
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
            from: number;
            to: number;
        };
    };
}

export interface ClassSubjectResponse {
    success: boolean;
    message: string;
    data: ClassSubject;
}

export const classSubjectApi = {
    async getAll(
        filters: ClassSubjectFilters = {},
    ): Promise<ClassSubjectListResponse> {
        const response = await api.get("/v1/class-subjects", {
            params: filters,
        });
        return response.data;
    },

    async getById(id: string): Promise<ClassSubjectResponse> {
        const response = await api.get(`/v1/class-subjects/${id}`);
        return response.data;
    },

    async create(data: CreateClassSubjectData): Promise<ClassSubjectResponse> {
        const response = await api.post("/v1/class-subjects", data);
        return response.data;
    },

    async update(
        id: string,
        data: UpdateClassSubjectData,
    ): Promise<ClassSubjectResponse> {
        const response = await api.put(`/v1/class-subjects/${id}`, data);
        return response.data;
    },

    async delete(id: string): Promise<{ success: boolean; message: string }> {
        await api.delete(`/v1/class-subjects/${id}`);
        return { success: true, message: "Class subject deleted successfully" };
    },

    async toggleStatus(id: string): Promise<ClassSubjectResponse> {
        const response = await api.post(
            `/v1/class-subjects/${id}/toggle-status`,
        );
        return response.data;
    },

    async getByClass(
        classId: string,
    ): Promise<{ success: boolean; message: string; data: ClassSubject[] }> {
        const response = await api.get(`/v1/class-subjects/class/${classId}`);
        return response.data;
    },

    async getByTeacher(
        teacherId: string,
    ): Promise<{ success: boolean; message: string; data: ClassSubject[] }> {
        const response = await api.get(
            `/v1/class-subjects/teacher/${teacherId}`,
        );
        return response.data;
    },

    async getBySemester(
        semesterId: string,
    ): Promise<{ success: boolean; message: string; data: ClassSubject[] }> {
        const response = await api.get(
            `/v1/class-subjects/semester/${semesterId}`,
        );
        return response.data;
    },
};

export default classSubjectApi;
